/**
 * REF-6877 (Phase D · D3a, user decision U2): the SVG (`get_vector_file`) and 4K (`upscale_image`) automatic
 * downloads belong to the tab that started the request.
 *
 * The BE fans every frame out to every open tab and replays terminals on reconnect, so `SocketListener` cannot
 * tell from a frame alone whether THIS tab asked for it. The initiators therefore record their own request's id
 * here when the start call answers, and the terminal handler acts only when it consumes a matching entry:
 * - the requesting tab downloads once (the entry is gone after the first terminal);
 * - another tab, a replayed copy and a reloaded page hold no entry, so they never download.
 *
 * The id is the one the BE returns for this request and repeats as the frame's `design_id` (REF-6868 D0b-2):
 * `convert-to-svg` → `responseData.design_id` (a uuid4 per request); `download-4k` → `responseData.job_id`.
 * Never the result URL — it is watermark-resolved per send. A single-purpose module set: one tab, one page load,
 * nothing but request ownership.
 */
export type OwnedDownloadKind = "get_vector_file" | "upscale_image";

const pending = new Set<string>();
const keyOf = (kind: OwnedDownloadKind, id: string) => `${kind}:${id}`;

/** Read the id a start response carries for `kind`; `undefined` when the response has none (handled, already-4K, …). */
const ownedDownloadIdOf = (kind: OwnedDownloadKind, response: any): string | undefined => {
  const payload = response?.responseData ?? response;
  const id = kind === "upscale_image" ? payload?.job_id : payload?.design_id;

  return typeof id === "string" && id !== "" ? id : undefined;
};

/** Record that this tab started the request `response` answers. A response without an id records nothing. */
export const rememberOwnedDownload = (kind: OwnedDownloadKind, response: any): void => {
  const id = ownedDownloadIdOf(kind, response);
  if (id) pending.add(keyOf(kind, id));
};

/** True exactly once per recorded request: removes the entry for this frame's `design_id` if this tab holds it. */
export const consumeOwnedDownload = (kind: OwnedDownloadKind, designId: unknown): boolean => typeof designId === "string" && designId !== "" && pending.delete(keyOf(kind, designId));
